import { useState, useRef, useEffect } from 'react';
import {
  Stack, Text, Group, Button, Box, Image, Loader, Progress,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { FileButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { IconPhoto, IconUpload, IconTrash } from '@tabler/icons-react';
import { FileStatusTypeId } from '@lib2/model/media/FileStatusTypeObj';
import type { PfileRow, PfileIds } from '@lib2/model/pfile/PfileTypes';
import { getPfiles, getFileIds } from '@lib2/model/pfile/PfileUtil';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { LinkUtils } from '@/features/link/LinkUtils';
import { UploadUtils } from '@/features/upload/UploadUtils';
import { usePfileImageUpload, useDeletePfileImage } from '@/shared/hooks/usePfileImageUpload';
import { usePfileImageProcessing } from '@/shared/hooks/usePfileImageProcessing';
import {
  PFILE_ACCEPTED_MIME_TYPES,
  PFILE_ACCEPTED_EXTENSIONS,
  PFILE_PROCESSING_TIMEOUT_MS,
  type PfileImageSpec,
} from '@/lib/constants/pfileImage';

interface PfileImageUploaderProps {
  pfiles: PfileRow[];
  spec: PfileImageSpec;
  pfileIds: PfileIds;
  invalidateQueryKeys: readonly (readonly unknown[])[];
  disabled?: boolean;
  containerHeight?: number;
}

/** Find the Ready pfile matching the spec's pfileTypeId. */
function getReadyPfile(pfiles: PfileRow[], spec: PfileImageSpec): PfileRow | null {
  return getPfiles(pfiles, [spec.pfileTypeId]).find(
    f => f.fileStatusTypeId === FileStatusTypeId.Ready && !f.deleted
  ) ?? null;
}

/** Check if any pfile of the target type is in a non-terminal processing state. */
function hasPendingPfile(pfiles: PfileRow[], spec: PfileImageSpec): boolean {
  return getPfiles(pfiles, [spec.pfileTypeId]).some(
    f =>
      !f.deleted &&
      f.fileStatusTypeId !== FileStatusTypeId.Ready &&
      f.fileStatusTypeId !== FileStatusTypeId.Deleted &&
      f.fileStatusTypeId !== FileStatusTypeId.Error
  );
}

export function PfileImageUploader({
  pfiles,
  spec,
  pfileIds,
  invalidateQueryKeys,
  disabled = false,
  containerHeight,
}: PfileImageUploaderProps) {
  const { data: linkConfig } = useSharedLinkConfig();
  const queryClient = useQueryClient();

  const uploadMutation = usePfileImageUpload(invalidateQueryKeys);
  const deleteMutation = useDeletePfileImage(invalidateQueryKeys);

  // Processing state — initialized from server data for reload-safety
  const isServerProcessing = hasPendingPfile(pfiles, spec);
  const [isProcessing, setIsProcessing] = useState(isServerProcessing);
  const [initiatedLocally, setInitiatedLocally] = useState(false);
  const [clientPreviewUrl, setClientPreviewUrl] = useState<string | null>(null);
  const ingestJobIdRef = useRef<number | null>(null);
  const hasNotifiedRef = useRef(false);
  const previousFileIdRef = useRef<number | null>(null);

  const jobStatus = usePfileImageProcessing(isProcessing, ingestJobIdRef.current);

  const readyPfile = getReadyPfile(pfiles, spec);
  const imageUrl = readyPfile && linkConfig
    ? LinkUtils.getAccountFileUrl(linkConfig, readyPfile.fileId, readyPfile.format)
    : null;
  const hasImage = readyPfile != null;
  const anyMutationPending = uploadMutation.isPending || deleteMutation.isPending;
  const maxFileSize = spec.maxFileSizeMb * 1024 * 1024;

  // Sync: detect externally triggered processing (e.g. background refetch)
  useEffect(() => {
    if (hasPendingPfile(pfiles, spec) && !isProcessing) {
      previousFileIdRef.current = readyPfile?.fileId ?? null;
      setIsProcessing(true);
      setInitiatedLocally(false);
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = false;
    }
  }, [pfiles, spec, isProcessing, readyPfile?.fileId]);

  // React to job status changes
  useEffect(() => {
    if (!jobStatus) return;

    if (jobStatus.phase === 'complete') {
      ingestJobIdRef.current = null;
      for (const qk of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: qk });
      }
    }

    if (jobStatus.phase === 'failed' && !hasNotifiedRef.current) {
      setIsProcessing(false);
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = true;

      if (initiatedLocally) {
        notifications.show({
          title: 'Image processing failed',
          message: `The ${spec.label.toLowerCase()} could not be processed. Please try again.`,
          color: 'red',
        });
      }
    }
  }, [jobStatus, queryClient, invalidateQueryKeys, initiatedLocally, spec.label]);

  // File-change detection: exit processing when a new Ready file appears
  useEffect(() => {
    if (!isProcessing) return;
    const ready = getReadyPfile(pfiles, spec);
    if (!ready) return;

    if (ready.fileId !== previousFileIdRef.current && !hasNotifiedRef.current) {
      setIsProcessing(false);
      previousFileIdRef.current = null;
      ingestJobIdRef.current = null;
      hasNotifiedRef.current = true;

      for (const qk of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: qk });
      }

      if (initiatedLocally) {
        notifications.show({
          title: `${spec.label} ready`,
          message: `Your ${spec.label.toLowerCase()} has been processed successfully.`,
          color: 'green',
        });
      }
    }
  }, [pfiles, spec, isProcessing, queryClient, invalidateQueryKeys, initiatedLocally]);

  // Safety timeout: prevent permanent spinner
  useEffect(() => {
    if (!isProcessing) return;
    const timer = setTimeout(() => {
      setIsProcessing(false);
      for (const qk of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: qk });
      }
    }, PFILE_PROCESSING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isProcessing, queryClient, invalidateQueryKeys]);

  // Cleanup client preview URL on unmount
  useEffect(() => {
    return () => {
      if (clientPreviewUrl) URL.revokeObjectURL(clientPreviewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isAcceptedFile(file: File): boolean {
    const mimeTypes: readonly string[] = PFILE_ACCEPTED_MIME_TYPES;
    if (file.type && mimeTypes.includes(file.type)) return true;
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return PFILE_ACCEPTED_EXTENSIONS.includes(ext as typeof PFILE_ACCEPTED_EXTENSIONS[number]);
  }

  /**
   * Reads pixel dimensions client-side. Returns null when the browser can't
   * decode the format (e.g. HEIC) — callers decide whether that blocks.
   */
  async function readImageDimensions(file: File): Promise<{ w: number; h: number } | null> {
    try {
      const bitmap = await createImageBitmap(file);
      const dims = { w: bitmap.width, h: bitmap.height };
      bitmap.close();
      return dims;
    } catch {
      return null;
    }
  }

  async function handleUploadFile(file: File) {
    if (!isAcceptedFile(file)) {
      notifications.show({
        title: 'Invalid file type',
        message: 'Please upload a JPG, PNG, WebP, or HEIC/HEIF image.',
        color: 'red',
      });
      return;
    }

    if (file.size > maxFileSize) {
      notifications.show({
        title: 'File too large',
        message: `Image must be under ${spec.maxFileSizeMb}MB.`,
        color: 'red',
      });
      return;
    }

    // Exact-size slots (Roku store/package assets) are blocked before upload —
    // off-spec dimensions fail store certification, not just look wrong.
    if (spec.exactSize) {
      const dims = await readImageDimensions(file);
      if (!dims) {
        notifications.show({
          title: 'Cannot read image size',
          message: 'This image format could not be checked. Use a PNG or JPG.',
          color: 'red',
        });
        return;
      }
      if (dims.w !== spec.exactSize.w || dims.h !== spec.exactSize.h) {
        notifications.show({
          title: 'Wrong image size',
          message: `${spec.label} must be exactly ${spec.exactSize.w} x ${spec.exactSize.h} pixels. This image is ${dims.w} x ${dims.h}.`,
          color: 'red',
        });
        return;
      }
    }

    // Snapshot current file so change-detection knows when a new one arrives
    previousFileIdRef.current = readyPfile?.fileId ?? null;

    const previewUrl = URL.createObjectURL(file);
    setClientPreviewUrl(previewUrl);

    const contentType = file.type || 'application/octet-stream';

    try {
      const ingestJobId = await uploadMutation.mutateAsync({
        file,
        contentType,
        pfileIds,
      });
      ingestJobIdRef.current = ingestJobId;
      setIsProcessing(true);
      setInitiatedLocally(true);
      hasNotifiedRef.current = false;
    } catch (err) {
      notifications.show({
        title: 'Upload failed',
        message: err instanceof Error ? err.message : 'Failed to upload image.',
        color: 'red',
      });
    } finally {
      URL.revokeObjectURL(previewUrl);
      setClientPreviewUrl(null);
    }
  }

  function handleDropzoneDrop(files: File[]) {
    if (files[0]) handleUploadFile(files[0]);
  }

  function handleDropzoneReject() {
    notifications.show({
      title: 'Invalid file',
      message: `Please upload a valid image file (JPG, PNG, WebP, HEIC/HEIF) under ${spec.maxFileSizeMb}MB.`,
      color: 'red',
    });
  }

  function handleDeleteClick() {
    const fileIds = getFileIds(pfiles, spec.deletePfileTypeIds);
    if (fileIds.length === 0) return;

    modals.openConfirmModal({
      title: `Delete ${spec.label}`,
      children: (
        <Text size="sm" c="dimmed">
          This will permanently remove the {spec.label.toLowerCase()}.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(fileIds);
          notifications.show({
            title: `${spec.label} deleted`,
            message: `The ${spec.label.toLowerCase()} has been removed.`,
            color: 'green',
          });
        } catch (err) {
          notifications.show({
            title: 'Delete failed',
            message: err instanceof Error ? err.message : `Failed to delete ${spec.label.toLowerCase()}.`,
            color: 'red',
          });
        }
      },
    });
  }

  const isDisabled = disabled || anyMutationPending;
  const showDropzone = !hasImage && !isProcessing && !clientPreviewUrl;
  const showClientPreview = clientPreviewUrl != null;
  const showProcessing = isProcessing && !clientPreviewUrl;
  const showImage = hasImage && !isProcessing && !clientPreviewUrl;

  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">{spec.label}</Text>

      {/* State A: Empty dropzone */}
      {showDropzone && (
        <Dropzone
          onDrop={handleDropzoneDrop}
          onReject={handleDropzoneReject}
          accept={PFILE_ACCEPTED_MIME_TYPES as unknown as string[]}
          maxSize={maxFileSize}
          maxFiles={1}
          disabled={isDisabled}
        >
          <Box
            w="100%"
            h={containerHeight}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack align="center" justify="center" gap="xs">
              <IconPhoto size={40} stroke={1.5} color="var(--mantine-color-gray-5)" />
              <Text size="sm" inline ta="center">
                Drop image here or click to browse
              </Text>
              <Text size="xs" c="dimmed" inline ta="center">
                JPG, PNG, WebP, HEIC/HEIF — Max {spec.maxFileSizeMb}MB
                — Recommended {spec.recommendedSize}
              </Text>
            </Stack>
          </Box>
        </Dropzone>
      )}

      {/* State B: Client-side preview during upload */}
      {showClientPreview && (
        <Box pos="relative">
          <Box
            w="100%"
            h={containerHeight}
            bg="gray.1"
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Image
              src={clientPreviewUrl}
              alt={`Uploading ${spec.label}`}
              fit="contain"
              h="100%"
              w="100%"
            />
          </Box>
          <Box
            pos="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            bg="rgba(255,255,255,0.7)"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-sm)',
            }}
          >
            <Stack align="center" gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">Uploading...</Text>
            </Stack>
          </Box>
        </Box>
      )}

      {/* State C: Processing indicator */}
      {showProcessing && (
        <Box
          w="100%"
          h={containerHeight}
          bg="gray.1"
          style={{
            borderRadius: 'var(--mantine-radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack align="center" justify="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {jobStatus?.phase === 'ingesting' ? 'Uploading...' : 'Processing...'}
            </Text>
            {jobStatus && jobStatus.progress > 0 && jobStatus.progress < 100 && (
              <Progress value={jobStatus.progress} size="sm" w={200} />
            )}
          </Stack>
        </Box>
      )}

      {/* State D: Image display */}
      {showImage && imageUrl && (
        <Stack gap="xs">
          <Box
            w="100%"
            h={containerHeight}
            bg="gray.1"
            style={{
              borderRadius: 'var(--mantine-radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Image
              src={imageUrl}
              alt={spec.label}
              fit="contain"
              h="100%"
              w="100%"
            />
          </Box>
          {readyPfile!.width && readyPfile!.height && (
            <Text size="xs" c="dimmed">
              {readyPfile!.width} x {readyPfile!.height}
              {readyPfile!.bytes > 0 && ` — ${UploadUtils.formatFileSize(readyPfile!.bytes)}`}
              {readyPfile!.format && ` — ${readyPfile!.format.toUpperCase()}`}
            </Text>
          )}
        </Stack>
      )}

      {/* Action buttons */}
      <Group gap="sm">
        <FileButton
          onChange={(file) => file && handleUploadFile(file)}
          accept={PFILE_ACCEPTED_MIME_TYPES.join(',')}
        >
          {(props) => (
            <Button
              {...props}
              variant="light"
              size="xs"
              leftSection={<IconUpload size={14} />}
              disabled={isProcessing || isDisabled}
            >
              {hasImage ? 'Replace' : 'Upload'}
            </Button>
          )}
        </FileButton>

        {hasImage && (
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={handleDeleteClick}
            disabled={isProcessing || isDisabled}
          >
            Delete
          </Button>
        )}
      </Group>
    </Stack>
  );
}
